/**
 * AiChatReferenceContext - provides a stable ref to a "reference clicked" handler.
 *
 * Lets a host surface make the chat's RAG reference list ([N] sources) clickable without the
 * generic ai-chat layer knowing what a reference opens. The deal workspace registers a handler
 * that opens the reference-estimate dialog; surfaces that don't register one (e.g. the repo
 * copilot) render the references as plain, non-clickable rows. Mirrors AiChatActionContext
 * (a ref, read at call-time, to dodge stale-closure capture in memoized parents).
 *
 * Usage:
 *   // Provider (host that owns the dialog):
 *   const refHandler = React.useRef<AiChatReferenceClickHandler | undefined>(undefined);
 *   refHandler.current = (ref) => openReferenceDialog(ref.referenceId);
 *   return <AiChatReferenceProvider handlerRef={refHandler}>...</AiChatReferenceProvider>;
 *
 *   // Consumer (RagReferenceList):
 *   const refHandler = useAiChatReferenceRef();
 *   const onClick = refHandler?.current ? () => refHandler.current?.(ref) : undefined;
 */

import type { RagReference } from '@protoss/shared';
import * as React from 'react';

export type AiChatReferenceClickHandler = (reference: RagReference) => void;

type HandlerRef = React.MutableRefObject<AiChatReferenceClickHandler | undefined>;

const AiChatReferenceContext = React.createContext<HandlerRef | null>(null);

export function AiChatReferenceProvider({
  children,
  handlerRef,
}: {
  children: React.ReactNode;
  handlerRef: HandlerRef;
}) {
  return (
    <AiChatReferenceContext.Provider value={handlerRef}>{children}</AiChatReferenceContext.Provider>
  );
}

/** Returns the stable ref object, or null when no host registered a handler. */
export function useAiChatReferenceRef(): HandlerRef | null {
  return React.useContext(AiChatReferenceContext);
}
